import React, { useEffect, useState } from "react"
import { IconButton, Stack, TextField, Tooltip, Typography } from "@mui/material"
import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import CheckIcon from "@mui/icons-material/Check"
import CloseIcon from "@mui/icons-material/Close"

interface Props {
  header: string
  value: string
  editable?: boolean
  editDisabled?: boolean
  editDisabledTooltip?: string
  onSave?: (value: string) => Promise<void>
  saving?: boolean
}

export const DrawerHeader = ({
  header,
  value,
  editable,
  editDisabled,
  editDisabledTooltip,
  onSave,
  saving
}: Props) => {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)

  useEffect(() => {
    if (!editing) {
      setDraft(value)
    }
  }, [value, editing])

  const handleStartEdit = () => {
    if (editDisabled) return
    setDraft(value)
    setEditing(true)
  }

  const handleCancel = () => {
    setDraft(value)
    setEditing(false)
  }

  const handleSave = async () => {
    if (!onSave || saving || editDisabled) return
    await onSave(draft)
    setEditing(false)
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault()
      void handleSave()
    }
    if (event.key === "Escape") {
      handleCancel()
    }
  }

  const editButton = (
    <IconButton
      size="small"
      aria-label="Edit project ID"
      onClick={handleStartEdit}
      disabled={editDisabled}
      sx={{ p: 0.25, flexShrink: 0 }}
    >
      <EditOutlinedIcon sx={{ fontSize: "1rem" }} />
    </IconButton>
  )

  return (
    <Stack spacing={1} sx={{ minWidth: 0, flex: 1 }}>
      <Typography
        variant={"body1"}
        sx={{
          fontSize: { xs: "0.75rem", md: "0.875rem" },
          fontWeight: "medium"
        }}
      >
        {header}
      </Typography>
      {editing ? (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            value={draft}
            disabled={saving}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={handleKeyDown}
            sx={{
              "& .MuiInputBase-input": {
                fontSize: { xs: "1.125rem", md: "1.5rem" },
                fontWeight: 600,
                lineHeight: 1.2
              }
            }}
          />
          <IconButton size="small" aria-label="Save project ID" onClick={() => void handleSave()} disabled={saving}>
            <CheckIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" aria-label="Cancel editing project ID" onClick={handleCancel} disabled={saving}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      ) : (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
          <Typography
            variant={"h5"}
            sx={{
              fontSize: { xs: "1.125rem", md: "1.5rem" },
              lineHeight: 1.2,
              fontWeight: "semibold",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "100%",
              minWidth: 0,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical"
            }}
          >
            {value}
          </Typography>
          {editable &&
            (editDisabled && editDisabledTooltip ? (
              <Tooltip title={editDisabledTooltip} arrow>
                <span>{editButton}</span>
              </Tooltip>
            ) : (
              editButton
            ))}
        </Stack>
      )}
    </Stack>
  )
}
